import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@base-ui/react/popover';
import { DayPicker, type DateRange } from 'react-day-picker';
import { ja } from 'react-day-picker/locale';
import { DateTime } from 'luxon';
import 'react-day-picker/style.css';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import {
  localDateToYmd,
  ymdToLocalDate,
} from '@components/Pages/AnalyticsPage/utils/dateRules';
import classes from './DateRangePicker.module.css';

type DisplayFormatter = (from: DateTime, to: DateTime, locale: string) => string;

/** A one-click range listed beside the calendar. */
export interface IDateRangePreset {
  label: string;
  dateFrom: string;
  dateTo: string;
  /** The dates it picks, shown muted beside the label (e.g. "Sep 21 – 27"). */
  hint?: string;
}

interface IDateRangePickerProps {
  /** YYYY-MM-DD start, interpreted as a calendar day in `tz`. */
  dateFrom: string;
  /** YYYY-MM-DD end (inclusive), interpreted as a calendar day in `tz`. */
  dateTo: string;
  onChange: (next: { dateFrom: string; dateTo: string }) => void;
  /** Project timezone — annotation only. Calendar interaction is tz-naive
      (see `ymdToLocalDate` / `localDateToYmd` helpers) so the YYYY-MM-DD
      string the user picks lines up with the same calendar day in `tz`.
      Omit for zone-free days (a plan's calendar dates). */
  tz?: string;
  ariaLabel: string;
  /** Trigger text while no range is picked (empty `dateFrom` / `dateTo`). */
  placeholder?: string;
  /** Optional custom trigger formatter. Default: `LLL d - LLL d`. */
  displayFormatter?: DisplayFormatter;
  /** Optional class merged onto the trigger button. */
  triggerClassName?: string;
  /** Quick ranges listed beside the calendar; picking one applies it and closes. */
  presets?: IDateRangePreset[];
  /** A "no range" option heading the presets (e.g. "All dates"): picking it
   *  calls `onClear` and closes. Shown only with `onClear`. */
  clearLabel?: string;
  onClear?: () => void;
  /** Portal target for the popup (a fullscreen host); default `document.body`. */
  container?: HTMLElement | null;
  /** Extra controls in a footer row under the presets and calendar (they don't
   *  close the popup) — for a picker that also carries related view settings. */
  footer?: ReactNode;
  /** Shown in the footer after a first calendar click, until the range's end is
   *  picked (e.g. "Pick an end date"). */
  pickEndHint?: string;
  /** First day of the calendar week: 0 = Sunday (default), 1 = Monday — match
   *  the surface the range drives (the Scheduler's weeks start on Monday). */
  weekStartsOn?: 0 | 1;
  /** Close once a range (or a preset) is picked — the default. Off, picks apply
   *  live and the popup stays open until an outside click or Escape. */
  closeOnSelect?: boolean;
}

/**
 * Pill-styled date range picker. Renders a calendar icon + display text
 * trigger, opens a two-month range picker. The trigger is the
 * pill-aesthetic counterpart to the field-style `DateRangeField`; both
 * share the same calendar popup chrome (composed from
 * `DatePicker/calendarStyles.module.css`).
 *
 * Tz-naive interaction model: the YYYY-MM-DD strings on the wire are
 * always interpreted as calendar days in the caller's project tz, but
 * react-day-picker itself operates in browser-local. `ymdToLocalDate`
 * constructs a browser-local midnight Date whose Y/M/D matches the wire
 * day, and on commit we read Y/M/D back off the JS Date directly. This
 * avoids the timezone-shift bugs that arise when `DateTime.fromJSDate(d)`
 * is given a Date carrying a different tz than the project's.
 */
export function DateRangePicker({
  dateFrom,
  dateTo,
  onChange,
  tz,
  ariaLabel,
  placeholder = '',
  displayFormatter,
  triggerClassName,
  presets,
  clearLabel,
  onClear,
  container,
  footer,
  pickEndHint,
  weekStartsOn = 0,
  closeOnSelect = true,
}: IDateRangePickerProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // A first calendar click picked a start; the next one picks the end.
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setAwaitingEnd(false);
  };
  const locale = i18n.language === 'ja' ? 'ja' : 'en';

  const fromDt = DateTime.fromISO(dateFrom, { zone: tz });
  const toDt = DateTime.fromISO(dateTo, { zone: tz });
  const format = displayFormatter ?? defaultDisplayFormatter;
  const display = fromDt.isValid && toDt.isValid ? format(fromDt, toDt, locale) : placeholder;

  // react-day-picker is browser-tz-naive: feed it Dates whose local Y/M/D
  // matches the calendar day the user expects to see highlighted.
  const fromDate = fromDt.isValid ? ymdToLocalDate(dateFrom) : undefined;
  const toDate = toDt.isValid ? ymdToLocalDate(dateTo) : undefined;

  // While a complete range is shown, a click starts a new one on that day
  // (the second click ends it) — react-day-picker would instead stretch the
  // shown range to the clicked day.
  const showingRange = fromDate && toDate && fromDate.getTime() !== toDate.getTime();
  const handleSelect = (picked: DateRange | undefined, day: Date) => {
    const range = showingRange ? { from: day, to: day } : picked;
    if (!range?.from || !range?.to) return;
    setAwaitingEnd(range.from.getTime() === range.to.getTime());
    onChange({
      dateFrom: localDateToYmd(range.from),
      dateTo: localDateToYmd(range.to),
    });
    if (closeOnSelect && range.from.getTime() !== range.to.getTime()) {
      setOpen(false);
    }
  };

  const triggerClass = triggerClassName
    ? `${classes.trigger} ${triggerClassName}`
    : classes.trigger;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger type="button" className={triggerClass} aria-label={ariaLabel}>
        <CardIcon name="calendar" size={14} />
        <span className={classes.label}>{display}</span>
      </Popover.Trigger>
      <Popover.Portal container={container ?? undefined}>
        <Popover.Positioner className={classes.positioner} sideOffset={6} align="start" collisionPadding={8}>
          {/* A pointer open focuses the popup itself — landing on the first
              option would paint a focus ring that reads as a selection. A
              keyboard open keeps the default (the first option). */}
          <Popover.Popup
            ref={popupRef}
            className={classes.popup}
            initialFocus={(openType) => (openType === 'keyboard' ? true : popupRef.current)}
          >
            <div className={classes.body}>
              {(presets || onClear) && (
                <div className={classes.presets}>
                  {onClear && (
                    <button
                      type="button"
                      className={`${classes.preset} ${classes.clearPreset}`}
                      aria-pressed={!dateFrom}
                      onClick={() => {
                        onClear();
                        setAwaitingEnd(false);
                        if (closeOnSelect) setOpen(false);
                      }}
                    >
                      {clearLabel}
                    </button>
                  )}
                  {presets?.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className={classes.preset}
                      aria-pressed={preset.dateFrom === dateFrom && preset.dateTo === dateTo}
                      onClick={() => {
                        onChange({ dateFrom: preset.dateFrom, dateTo: preset.dateTo });
                        setAwaitingEnd(false);
                        if (closeOnSelect) setOpen(false);
                      }}
                    >
                      <span>{preset.label}</span>
                      {preset.hint && <span className={classes.presetHint}>{preset.hint}</span>}
                    </button>
                  ))}
                </div>
              )}
              <DayPicker
                mode="range"
                selected={{ from: fromDate, to: toDate }}
                defaultMonth={fromDate}
                onSelect={handleSelect}
                numberOfMonths={2}
                weekStartsOn={weekStartsOn}
                locale={locale === 'ja' ? ja : undefined}
              />
            </div>
            {(footer || pickEndHint) && (
              <div className={classes.footer}>
                <span className={classes.footerHint} aria-live="polite">
                  {awaitingEnd ? pickEndHint : ''}
                </span>
                {footer}
              </div>
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

function defaultDisplayFormatter(from: DateTime, to: DateTime, locale: string): string {
  return `${from.setLocale(locale).toFormat('LLL d')} - ${to.setLocale(locale).toFormat('LLL d')}`;
}
